import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Badge } from "../components/Badge";
import { toast } from "sonner";
import {
  ShieldCheck, ShieldAlert, Search, Trash2, CheckCircle2, RotateCcw, Plus,
  Smartphone, Globe, Users, Fingerprint, AlertTriangle, ListChecks, Loader2,
  Copy, Ban, UserPlus, KeyRound,
} from "lucide-react";

const EMPTY_STATS = { total_registrations: 0, unique_devices: 0, unique_ips: 0, flagged_devices: 0, flagged_ips: 0, whitelisted: 0, blocked: 0 };

export default function AntiFraud() {
  const [cfg, setCfg] = useState({ turnstile_enabled: true, anti_fraud_enabled: true, device_limit: 1, ip_limit: 3, registration_enabled: true, registration_referral_only: false, quick_signup_enabled: true, quick_signup_guard_enabled: true });
  const [stats, setStats] = useState(EMPTY_STATS);
  const [rows, setRows] = useState([]);
  const [whitelist, setWhitelist] = useState([]);
  const [blocklist, setBlocklist] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState("");

  const loadCfg = useCallback(async () => { const { data } = await api.get("/admin/anti-fraud/settings"); setCfg(data); }, []);
  const loadStats = useCallback(async () => { const { data } = await api.get("/admin/anti-fraud/stats"); setStats(data); }, []);
  const loadWl = useCallback(async () => { const { data } = await api.get("/admin/anti-fraud/whitelist"); setWhitelist(data); }, []);
  const loadBl = useCallback(async () => { const { data } = await api.get("/admin/anti-fraud/blocklist"); setBlocklist(data); }, []);
  const loadRows = useCallback(async (s = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/anti-fraud/registrations", { params: { search: s } });
      setRows(data.registrations || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadCfg(); loadStats(); loadWl(); loadBl(); loadRows(); }, [loadCfg, loadStats, loadWl, loadBl, loadRows]);

  const copyValue = async (value, label = "Device ID") => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`, { description: value });
    } catch {
      // Fallback for non-secure contexts
      const ta = document.createElement("textarea");
      ta.value = value; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); toast.success(`${label} copied`); } catch { toast.error("Copy failed — select manually"); }
      document.body.removeChild(ta);
    }
  };

  // Instant-save a single setting (on/off toggles + limit steppers auto-persist)
  const patchCfg = async (key, value, uiKey) => {
    const prev = cfg;
    setCfg((c) => ({ ...c, [key]: value }));
    setSavingKey(uiKey);
    try {
      const { data } = await api.post("/admin/anti-fraud/settings", { [key]: value });
      setCfg(data);
      toast.success("Saved", { description: LABELS[uiKey] });
    } catch {
      setCfg(prev);
      toast.error("Could not save");
    } finally { setSavingKey(""); }
  };

  const addWhitelist = async (type, value, note = "") => {
    if (!value) return;
    try {
      await api.post("/admin/anti-fraud/whitelist", { type, value, note });
      toast.success(`${type === "device" ? "Device" : "IP"} allowed — can now open extra accounts`);
      loadWl(); loadRows(search); loadStats();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const delWhitelist = async (id) => { await api.delete(`/admin/anti-fraud/whitelist/${id}`); toast.success("Removed"); loadWl(); loadRows(search); loadStats(); };
  const addBlocklist = async (type, value, note = "") => {
    if (!value) return;
    try {
      await api.post("/admin/anti-fraud/blocklist", { type, value, note });
      toast.success(`${type === "device" ? "Device" : "IP"} banned`);
      loadBl(); loadRows(search); loadStats();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const delBlocklist = async (id) => { await api.delete(`/admin/anti-fraud/blocklist/${id}`); toast.success("Unbanned"); loadBl(); loadRows(search); loadStats(); };
  const resetBlock = async (type, value) => {
    if (!window.confirm(`Unblock this ${type}?\n${value}\n\nTheir recorded registrations will be cleared so they can sign up again.`)) return;
    const { data } = await api.post("/admin/anti-fraud/reset", { type, value });
    toast.success(`Unblocked — cleared ${data.removed} record(s)`);
    loadRows(search); loadStats();
  };

  const [blType, setBlType] = useState("device");
  const [blValue, setBlValue] = useState("");
  const [blNote, setBlNote] = useState("");

  const [wlType, setWlType] = useState("device");
  const [wlValue, setWlValue] = useState("");
  const [wlNote, setWlNote] = useState("");

  return (
    <div data-testid="anti-fraud-page">
      <PageHeader
        title="Anti-Fraud & Registration Guard"
        subtitle="Stop fake accounts from farming the registration bonus. Cloudflare captcha blocks bots; the device/IP guard limits how many accounts one phone or network can open. Whitelist genuine users who need extra accounts, or unblock anyone caught by mistake."
      />

      {/* ── Stat cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-5">
        <Stat icon={Users} label="Accounts tracked" value={stats.total_registrations} tone="slate" />
        <Stat icon={Fingerprint} label="Unique devices" value={stats.unique_devices} tone="blue" />
        <Stat icon={Globe} label="Unique IPs" value={stats.unique_ips} tone="indigo" />
        <Stat icon={AlertTriangle} label="Flagged devices" value={stats.flagged_devices} tone="red" hint="device with 2+ accounts" />
        <Stat icon={ListChecks} label="Whitelisted" value={stats.whitelisted} tone="emerald" />
        <Stat icon={Ban} label="Banned" value={stats.blocked} tone="red" hint="device/IP hard-blocked" />
      </div>

      {/* ── Protection settings (instant on/off) ── */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-5">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2 text-gray-800 font-semibold">
          <ShieldCheck size={16} className="text-emerald-600" /> Protection settings
          <span className="text-xs font-normal text-gray-400 ml-1">changes save instantly</span>
        </div>
        <div className="divide-y divide-gray-100">
          <SwitchRow
            icon={UserPlus}
            title="New registration open"
            desc="Master switch. Turn OFF to close all new signups (maintenance mode)."
            checked={cfg.registration_enabled}
            saving={savingKey === "reg_enabled"}
            onToggle={(v) => patchCfg("registration_enabled", v, "reg_enabled")}
            testId="af-toggle-reg-enabled"
          />
          <SwitchRow
            icon={KeyRound}
            title="Referral code required"
            desc="Only allow new accounts that come with a valid referral / promo code."
            checked={cfg.registration_referral_only}
            saving={savingKey === "reg_ref_only"}
            onToggle={(v) => patchCfg("registration_referral_only", v, "reg_ref_only")}
            testId="af-toggle-referral-only"
          />
          <SwitchRow
            icon={ShieldCheck}
            title="Cloudflare captcha on signup"
            desc="Every new account must pass the “I'm not a robot” check."
            checked={cfg.turnstile_enabled}
            saving={savingKey === "turnstile"}
            onToggle={(v) => patchCfg("turnstile_enabled", v, "turnstile")}
            testId="af-toggle-turnstile"
          />
          <SwitchRow
            icon={ShieldAlert}
            title="Device & IP guard"
            desc="Block duplicate accounts from the same phone/browser or network."
            checked={cfg.anti_fraud_enabled}
            saving={savingKey === "guard"}
            onToggle={(v) => patchCfg("anti_fraud_enabled", v, "guard")}
            testId="af-toggle-guard"
          />
          <StepperRow
            title="Max accounts per device"
            desc="How many accounts a single device may open."
            value={cfg.device_limit}
            saving={savingKey === "device_limit"}
            disabled={!cfg.anti_fraud_enabled}
            onChange={(v) => patchCfg("device_limit", v, "device_limit")}
            testId="af-device-limit"
          />
          <StepperRow
            title="Max accounts per IP"
            desc="How many accounts one network/IP may open (homes often share an IP)."
            value={cfg.ip_limit}
            saving={savingKey === "ip_limit"}
            disabled={!cfg.anti_fraud_enabled}
            onChange={(v) => patchCfg("ip_limit", v, "ip_limit")}
            testId="af-ip-limit"
          />
          <SwitchRow
            icon={ShieldCheck}
            title="One-click signup enabled"
            desc="Allow the instant “one-tap” account creation on the register page."
            checked={cfg.quick_signup_enabled}
            saving={savingKey === "quick_enabled"}
            onToggle={(v) => patchCfg("quick_signup_enabled", v, "quick_enabled")}
            testId="af-toggle-quick-enabled"
          />
          <SwitchRow
            icon={ShieldAlert}
            title="Device & IP guard on one-click signup"
            desc="Also apply the per-device / per-IP limit to one-click signups."
            checked={cfg.quick_signup_guard_enabled}
            saving={savingKey === "quick_guard"}
            disabled={!cfg.quick_signup_enabled}
            onToggle={(v) => patchCfg("quick_signup_guard_enabled", v, "quick_guard")}
            testId="af-toggle-quick-guard"
          />
        </div>
      </div>

      {/* ── Whitelist ── */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 mb-5">
        <div className="text-gray-800 font-semibold mb-1">Allow extra accounts (whitelist)</div>
        <div className="text-xs text-gray-400 mb-3">Whitelist a device or IP when a genuine user (e.g. family, agent) needs more than one account. Tip: copy the Device ID from the table below (📋 button) and paste it here.</div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Type</label>
            <select value={wlType} onChange={(e) => setWlType(e.target.value)} className="h-10 px-3 border border-gray-200 rounded-md text-sm bg-white" data-testid="af-wl-type">
              <option value="device">Device</option>
              <option value="ip">IP</option>
            </select>
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">{wlType === "device" ? "Device ID" : "IP address"}</label>
            <input value={wlValue} onChange={(e) => setWlValue(e.target.value)} placeholder={wlType === "device" ? "paste device id" : "e.g. 103.x.x.x"} className="w-full h-10 px-3 border border-gray-200 rounded-md text-sm" data-testid="af-wl-value" />
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Note (optional)</label>
            <input value={wlNote} onChange={(e) => setWlNote(e.target.value)} placeholder="reason" className="w-full h-10 px-3 border border-gray-200 rounded-md text-sm" data-testid="af-wl-note" />
          </div>
          <button onClick={() => { addWhitelist(wlType, wlValue.trim(), wlNote.trim()); setWlValue(""); setWlNote(""); }} className="h-10 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-semibold inline-flex items-center gap-1.5" data-testid="af-wl-add">
            <Plus size={14} /> Allow
          </button>
        </div>
        {whitelist.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="af-wl-list">
            {whitelist.map((w) => (
              <span key={w.id} className="inline-flex items-center gap-1.5 h-8 pl-2 pr-1 border border-emerald-200 bg-emerald-50 text-emerald-800 rounded-md text-xs">
                {w.type === "device" ? <Smartphone size={12} /> : <Globe size={12} />}
                <span className="font-mono">{w.value.length > 24 ? w.value.slice(0, 24) + "…" : w.value}</span>
                {w.note && <span className="text-emerald-600">· {w.note}</span>}
                <button onClick={() => delWhitelist(w.id)} className="h-6 w-6 inline-flex items-center justify-center hover:text-red-600" title="Remove"><Trash2 size={12} /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── Blocklist (hard ban) ── */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 mb-5">
        <div className="text-gray-800 font-semibold mb-1 flex items-center gap-1.5"><Ban size={15} className="text-red-600" /> Ban device / IP (blocklist)</div>
        <div className="text-xs text-gray-400 mb-3">A banned device or IP can NEVER register — even if the guard is off or the limit isn't reached. Use this to permanently block abusers.</div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Type</label>
            <select value={blType} onChange={(e) => setBlType(e.target.value)} className="h-10 px-3 border border-gray-200 rounded-md text-sm bg-white" data-testid="af-bl-type">
              <option value="device">Device</option>
              <option value="ip">IP</option>
            </select>
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">{blType === "device" ? "Device ID" : "IP address"}</label>
            <input value={blValue} onChange={(e) => setBlValue(e.target.value)} placeholder={blType === "device" ? "paste device id" : "e.g. 103.x.x.x"} className="w-full h-10 px-3 border border-gray-200 rounded-md text-sm" data-testid="af-bl-value" />
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Note (optional)</label>
            <input value={blNote} onChange={(e) => setBlNote(e.target.value)} placeholder="reason" className="w-full h-10 px-3 border border-gray-200 rounded-md text-sm" data-testid="af-bl-note" />
          </div>
          <button onClick={() => { addBlocklist(blType, blValue.trim(), blNote.trim()); setBlValue(""); setBlNote(""); }} className="h-10 px-4 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-semibold inline-flex items-center gap-1.5" data-testid="af-bl-add">
            <Ban size={14} /> Ban
          </button>
        </div>
        {blocklist.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="af-bl-list">
            {blocklist.map((b) => (
              <span key={b.id} className="inline-flex items-center gap-1.5 h-8 pl-2 pr-1 border border-red-200 bg-red-50 text-red-800 rounded-md text-xs">
                {b.type === "device" ? <Smartphone size={12} /> : <Globe size={12} />}
                <span className="font-mono">{b.value.length > 24 ? b.value.slice(0, 24) + "…" : b.value}</span>
                {b.note && <span className="text-red-500">· {b.note}</span>}
                <button onClick={() => delBlocklist(b.id)} className="h-6 w-6 inline-flex items-center justify-center hover:text-emerald-600" title="Remove ban"><Trash2 size={12} /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── Registrations ── */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex items-center gap-2">
          <div className="text-gray-800 font-semibold mr-auto">Recent registrations</div>
          <div className="relative w-full max-w-xs">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadRows(search)} placeholder="Search device, IP, username…" className="w-full h-9 pl-9 pr-3 border border-gray-200 rounded-md text-sm" data-testid="af-search" />
          </div>
          <button onClick={() => loadRows(search)} className="h-9 px-3 border border-gray-200 rounded-md text-sm hover:bg-gray-50">Search</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold">User</th>
                <th className="text-left px-4 py-2.5 font-semibold">Device</th>
                <th className="text-left px-4 py-2.5 font-semibold">IP</th>
                <th className="text-left px-4 py-2.5 font-semibold">When</th>
                <th className="text-right px-4 py-2.5 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="af-rows">
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-400"><Loader2 className="animate-spin inline" size={18} /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-400">No registrations recorded yet.</td></tr>
              ) : rows.map((r, i) => (
                <tr key={i} className="border-t border-gray-100 hover:bg-gray-50/70" data-testid={`af-row-${i}`}>
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-gray-900">{r.username || "—"}</div>
                    <div className="text-xs text-gray-400 font-mono">{r.player_id || ""}</div>
                  </td>
                  <td className="px-4 py-2.5">
                    {r.device_id ? (
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-xs text-gray-700 break-all max-w-[220px]" title={r.device_id}>{r.device_id}</span>
                        <button onClick={() => copyValue(r.device_id, "Device ID")} className="h-6 w-6 shrink-0 inline-flex items-center justify-center text-gray-400 hover:text-blue-600 rounded" title="Copy full Device ID" data-testid={`af-copy-device-${i}`}><Copy size={13} /></button>
                      </div>
                    ) : <span className="font-mono text-xs text-gray-400">—</span>}
                    <div className="flex items-center gap-1 mt-1 flex-wrap">
                      {r.device_account_count > 1 && <Badge variant="danger">{r.device_account_count} accts</Badge>}
                      {r.device_whitelisted && <Badge variant="success">allowed</Badge>}
                      {r.device_blocked && <Badge variant="danger">banned</Badge>}
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    {r.ip ? (
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-xs text-gray-700" title={r.ip}>{r.ip}</span>
                        <button onClick={() => copyValue(r.ip, "IP")} className="h-6 w-6 shrink-0 inline-flex items-center justify-center text-gray-400 hover:text-blue-600 rounded" title="Copy IP" data-testid={`af-copy-ip-${i}`}><Copy size={13} /></button>
                      </div>
                    ) : <span className="font-mono text-xs text-gray-400">—</span>}
                    <div className="flex items-center gap-1 mt-1 flex-wrap">
                      {r.ip_account_count > 1 && <Badge variant="pending">{r.ip_account_count} accts</Badge>}
                      {r.ip_whitelisted && <Badge variant="success">allowed</Badge>}
                      {r.ip_blocked && <Badge variant="danger">banned</Badge>}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-1 flex-wrap">
                      {r.device_id && !r.device_whitelisted && (
                        <button onClick={() => addWhitelist("device", r.device_id)} className="h-7 px-2 text-xs border border-emerald-200 text-emerald-700 rounded-md inline-flex items-center gap-1 hover:bg-emerald-50" title="Allow this device to open extra accounts" data-testid={`af-allow-device-${i}`}><CheckCircle2 size={12} /> Allow multi</button>
                      )}
                      {r.device_id && !r.device_blocked && (
                        <button onClick={() => addBlocklist("device", r.device_id)} className="h-7 px-2 text-xs border border-red-200 text-red-700 rounded-md inline-flex items-center gap-1 hover:bg-red-50" title="Permanently ban this device" data-testid={`af-ban-device-${i}`}><Ban size={12} /> Ban dev</button>
                      )}
                      {r.ip && !r.ip_blocked && (
                        <button onClick={() => addBlocklist("ip", r.ip)} className="h-7 px-2 text-xs border border-red-200 text-red-700 rounded-md inline-flex items-center gap-1 hover:bg-red-50" title="Permanently ban this IP" data-testid={`af-ban-ip-${i}`}><Ban size={12} /> Ban IP</button>
                      )}
                      {r.device_id && (
                        <button onClick={() => resetBlock("device", r.device_id)} className="h-7 px-2 text-xs border border-gray-200 text-gray-600 rounded-md inline-flex items-center gap-1 hover:bg-gray-100" title="Clear this device's registrations so they can sign up again"><RotateCcw size={12} /> Reset dev</button>
                      )}
                      {r.ip && (
                        <button onClick={() => resetBlock("ip", r.ip)} className="h-7 px-2 text-xs border border-gray-200 text-gray-600 rounded-md inline-flex items-center gap-1 hover:bg-gray-100" title="Clear this IP's registrations"><RotateCcw size={12} /> Reset IP</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const LABELS = {
  turnstile: "Cloudflare captcha",
  guard: "Device & IP guard",
  device_limit: "Max accounts per device",
  ip_limit: "Max accounts per IP",
  reg_enabled: "New registration open",
  reg_ref_only: "Referral code required",
  quick_enabled: "One-click signup",
  quick_guard: "Guard on one-click signup",
};

const TONES = {
  slate: "bg-slate-50 text-slate-600 border-slate-200",
  blue: "bg-blue-50 text-blue-600 border-blue-200",
  indigo: "bg-indigo-50 text-indigo-600 border-indigo-200",
  red: "bg-red-50 text-red-600 border-red-200",
  emerald: "bg-emerald-50 text-emerald-600 border-emerald-200",
};

function Stat({ icon: Icon, label, value, tone = "slate", hint }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className={`w-9 h-9 rounded-lg border flex items-center justify-center mb-2 ${TONES[tone]}`}>
        <Icon size={16} />
      </div>
      <div className="text-2xl font-black text-gray-900 leading-none">{value}</div>
      <div className="text-xs text-gray-500 mt-1">{label}</div>
      {hint && <div className="text-[10px] text-gray-400 mt-0.5">{hint}</div>}
    </div>
  );
}

function SwitchRow({ icon: Icon, title, desc, checked, saving, onToggle, testId }) {
  return (
    <div className="flex items-center gap-3 px-5 py-4">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${checked ? "bg-emerald-50 text-emerald-600" : "bg-gray-100 text-gray-400"}`}>
        <Icon size={16} />
      </div>
      <div className="min-w-0 mr-auto">
        <div className="font-semibold text-gray-800 text-sm">{title}</div>
        <div className="text-xs text-gray-500">{desc}</div>
      </div>
      <span className={`text-xs font-semibold ${checked ? "text-emerald-600" : "text-gray-400"}`}>{saving ? "…" : checked ? "ON" : "OFF"}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => !saving && onToggle(!checked)}
        data-testid={testId}
        className={`relative w-12 h-7 rounded-full transition-colors shrink-0 ${checked ? "bg-emerald-500" : "bg-gray-300"} ${saving ? "opacity-60" : ""}`}
      >
        <span className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-all ${checked ? "left-6" : "left-1"}`} />
      </button>
    </div>
  );
}

function StepperRow({ title, desc, value, saving, disabled, onChange, testId }) {
  return (
    <div className={`flex items-center gap-3 px-5 py-4 ${disabled ? "opacity-50" : ""}`}>
      <div className="min-w-0 mr-auto">
        <div className="font-semibold text-gray-800 text-sm">{title}</div>
        <div className="text-xs text-gray-500">{desc}</div>
      </div>
      <div className="flex items-center gap-1" data-testid={testId}>
        <button type="button" disabled={disabled || saving || value <= 1} onClick={() => onChange(value - 1)} className="w-8 h-8 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-30 font-bold">−</button>
        <div className="w-12 text-center font-black text-gray-900">{saving ? <Loader2 className="animate-spin inline" size={14} /> : value}</div>
        <button type="button" disabled={disabled || saving} onClick={() => onChange(value + 1)} className="w-8 h-8 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-30 font-bold">+</button>
      </div>
    </div>
  );
}
